import React, { useMemo, useRef, useState } from "react";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import {
  BRANDS,
  PILLAR_META,
  STATUS_META,
  PILLAR_ORDER,
  prettyDomain,
  withOutboundUtm,
} from "@/data/mera-brands";

// Cross-component signal: Foundry → Waitlist. Waitlist listens for this
// custom event and pre-selects the requested brand chip. We also smooth-scroll
// the user down to the form.
const requestWaitlist = (brandId) => {
  try {
    window.dispatchEvent(
      new CustomEvent("mera:waitlist-preselect", { detail: { brand: brandId } })
    );
  } catch {
    /* older browsers — no-op */
  }
  const el = document.getElementById("waitlist");
  if (el) {
    const offset = 96;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
  }
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status];
  if (!meta) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 font-mono-label text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 border"
      style={{
        color: meta.color,
        borderColor: `${meta.color}55`,
        backgroundColor: `${meta.color}0F`,
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: meta.color }}
      />
      {meta.label}
    </span>
  );
};

const AccordionRow = ({ b, isOpen, onToggle, activePillar }) => {
  const inPillar = activePillar === "all" || b.pillar === activePillar;
  const pillarMeta = PILLAR_META[b.pillar];

  return (
    <article
      id={`brand-${b.id}`}
      data-testid={`brand-row-${b.id}`}
      data-pillar={b.pillar}
      className={`relative border-b border-black/10 transition-opacity duration-500 ${
        inPillar ? "opacity-100" : "opacity-30"
      }`}
      style={{
        backgroundColor: isOpen ? `${b.accent}08` : "transparent",
      }}
    >
      {/* Persistent left pillar stripe */}
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-[3px] transition-all duration-500"
        style={{
          backgroundColor: pillarMeta.color,
          opacity: isOpen ? 1 : inPillar ? 0.55 : 0.15,
        }}
      />

      <button
        type="button"
        data-testid={`brand-toggle-${b.id}`}
        aria-expanded={isOpen}
        aria-controls={`brand-body-${b.id}`}
        onClick={onToggle}
        className="w-full grid grid-cols-12 items-center gap-4 md:gap-6 px-5 md:px-8 py-6 md:py-7 text-left group"
      >
        {/* Left: pillar dot + name */}
        <div className="col-span-8 md:col-span-3 flex items-center gap-3 md:gap-4 min-w-0">
          <span
            className="inline-block w-2 h-2 rounded-full shrink-0 transition-transform duration-300"
            style={{
              backgroundColor: pillarMeta.color,
              transform: isOpen ? "scale(1.6)" : "scale(1)",
            }}
          />
          <h3
            className="font-serif-display text-[24px] md:text-[32px] leading-none tracking-tight transition-colors duration-500 truncate"
            style={{ color: isOpen ? b.accent : "var(--mera-ink)" }}
          >
            {b.name}
          </h3>
        </div>

        {/* Middle: tagline */}
        <p className="hidden md:block col-span-6 font-serif-display italic text-[15px] leading-snug text-black/70">
          {b.tagline}
        </p>

        {/* Right: pillar label + status + chevron */}
        <div className="col-span-4 md:col-span-3 flex items-center justify-end gap-2 md:gap-3">
          <span
            className="hidden md:inline font-mono-label text-[10px] uppercase tracking-[0.18em]"
            style={{ color: pillarMeta.color }}
          >
            {pillarMeta.label}
          </span>
          <StatusBadge status={b.status} />
          <ChevronDown
            size={16}
            strokeWidth={1.5}
            className="text-black/45 transition-transform duration-500 shrink-0"
            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </div>

        {/* Mobile tagline row */}
        <p className="md:hidden col-span-12 -mt-2 font-serif-display italic text-[14px] leading-snug text-black/70">
          {b.tagline}
        </p>
      </button>

      {/* Expanded body */}
      <div
        id={`brand-body-${b.id}`}
        data-testid={`brand-body-${b.id}`}
        role="region"
        aria-hidden={!isOpen}
        className={`overflow-hidden transition-[grid-template-rows] duration-500 ease-out grid ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="min-h-0">
          <div className="px-5 md:px-8 pb-10 md:pb-12 pt-2 md:pt-2 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-8">
            <div>
              <div className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-black/50 mb-2">
                What it does
              </div>
              <p className="text-[15px] leading-relaxed text-black/75">
                {b.whatItDoes}
              </p>
            </div>
            <div>
              <div className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-black/50 mb-2">
                Who it serves
              </div>
              <p className="text-[15px] leading-relaxed text-black/75">
                {b.whoItServes}
              </p>
            </div>
            <div>
              <div className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-black/50 mb-2">
                Status
              </div>
              <p className="text-[15px] leading-relaxed text-black/75">
                {b.statusDetail}
              </p>
            </div>
            <div>
              <div className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-black/50 mb-2">
                Pillar
              </div>
              <p className="text-[15px] leading-relaxed text-black/75">
                {b.pillarDescription}
              </p>
            </div>

            {/* Pull-quote highlight bar */}
            <div
              className="col-span-1 md:col-span-2 mt-2 px-6 py-5 border-l-2"
              style={{
                borderLeftColor: b.accent,
                backgroundColor: `${b.accent}0D`,
              }}
            >
              <p className="font-serif-display italic text-[19px] md:text-[22px] leading-snug text-black/85">
                “{b.quote}”
              </p>
            </div>

            {/* Action row — primary CTA + optional live-product link */}
            <div
              data-testid={`brand-actions-${b.id}`}
              className="col-span-1 md:col-span-2 mt-2 flex flex-wrap items-center gap-x-8 gap-y-3"
            >
              <button
                type="button"
                data-testid={`brand-reserve-${b.id}`}
                onClick={() => requestWaitlist(b.id)}
                className="group inline-flex items-center gap-2 font-mono-label text-[11px] uppercase tracking-[0.18em] px-4 py-2 border transition-all duration-300"
                style={{
                  color: b.accent,
                  borderColor: `${b.accent}66`,
                  backgroundColor: `${b.accent}0F`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = b.accent;
                  e.currentTarget.style.color = "#F7F7F6";
                  e.currentTarget.style.borderColor = b.accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = `${b.accent}0F`;
                  e.currentTarget.style.color = b.accent;
                  e.currentTarget.style.borderColor = `${b.accent}66`;
                }}
              >
                <span
                  aria-hidden
                  className="w-1.5 h-1.5 rounded-full transition-colors"
                  style={{ backgroundColor: "currentColor" }}
                />
                Reserve early access
                <ArrowUpRight
                  size={14}
                  strokeWidth={1.5}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </button>

              {b.url && (
                <a
                  href={withOutboundUtm(b.url, b.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`brand-visit-${b.id}`}
                  className="inline-flex items-center gap-1.5 font-mono-label text-[11px] uppercase tracking-[0.18em] text-black/60 hover:text-black transition-colors"
                >
                  Visit {prettyDomain(b.url)}
                  <ArrowUpRight size={12} strokeWidth={1.5} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export const Foundry = () => {
  const [openId, setOpenId] = useState(null);
  const [activePillar, setActivePillar] = useState("all");
  const gridRef = useRef(null);

  const onToggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  const onPillarClick = (key) => {
    setActivePillar(key);
    if (key === "all") {
      gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const first = BRANDS.find((b) => b.pillar === key);
    if (first) {
      const el = document.getElementById(`brand-${first.id}`);
      if (el) {
        const offset = 160;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }
  };

  const pillarCounts = useMemo(
    () =>
      PILLAR_ORDER.reduce((acc, k) => {
        acc[k] =
          k === "all" ? BRANDS.length : BRANDS.filter((b) => b.pillar === k).length;
        return acc;
      }, {}),
    []
  );

  return (
    <section
      id="foundry"
      data-testid="foundry-section"
      className="relative px-6 md:px-10 py-24 md:py-32"
    >
      {/* Section header */}
      <div className="grid grid-cols-12 gap-6 mb-14 md:mb-16">
        <div className="col-span-12 md:col-span-3">
          <div className="font-mono-label text-[11px] text-black/55">
            § 02 · The Ecosystem
          </div>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h2 className="font-serif-display text-[44px] md:text-[72px] leading-[1.02] tracking-tight">
            Ten products. <span className="italic-accent">One architecture.</span>
            <br />
            One philosophy.
          </h2>
          <p className="mt-6 max-w-[720px] text-black/65 text-[16px] leading-relaxed">
            We do not ship features. We ship instruments — precise, compounding,
            and built to last. Each product stands complete on its own. Use one.
            Use all ten. Either way, it works.
          </p>
        </div>
      </div>

      {/* Sticky pillar wayfinder */}
      <div
        data-testid="pillar-wayfinder"
        className="sticky top-[72px] z-30 -mx-6 md:-mx-10 mb-8 backdrop-blur-xl bg-[rgba(247,247,246,0.88)] border-y border-black/10"
        style={{ ["--active-pillar"]: PILLAR_META[activePillar].color }}
      >
        <span
          aria-hidden
          className="absolute left-0 right-0 top-0 h-[2px] transition-colors duration-500"
          style={{
            backgroundColor:
              activePillar === "all" ? "transparent" : "var(--active-pillar)",
          }}
        />
        <div className="px-6 md:px-10 py-4 flex items-center gap-3 overflow-x-auto whitespace-nowrap scrollbar-none">
          <div className="font-mono-label text-[10px] text-black/45 hidden md:inline shrink-0">
            Browse by pillar →
          </div>
          {PILLAR_ORDER.map((key) => {
            const meta = PILLAR_META[key];
            const active = activePillar === key;
            return (
              <button
                key={key}
                type="button"
                data-testid={`pillar-chip-${key}`}
                aria-pressed={active}
                onClick={() => onPillarClick(key)}
                className="group relative inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-[11px] font-mono-label uppercase tracking-[0.18em] transition-all duration-300 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#F7F7F6] hover:scale-[1.02] active:scale-[0.97]"
                style={{
                  borderColor: active ? meta.color : "rgba(10,10,10,0.18)",
                  backgroundColor: active ? meta.color : "transparent",
                  color: active ? "#F7F7F6" : "rgba(10,10,10,0.7)",
                  ["--tw-ring-color"]: meta.color,
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.borderColor = meta.color;
                    e.currentTarget.style.color = meta.color;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.borderColor = "rgba(10,10,10,0.18)";
                    e.currentTarget.style.color = "rgba(10,10,10,0.7)";
                  }
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full transition-transform duration-300"
                  style={{
                    backgroundColor: active ? "#F7F7F6" : meta.color,
                    transform: active ? "scale(1.4)" : "scale(1)",
                  }}
                />
                {meta.label}
                <span
                  className="ml-1 text-[9px] tabular-nums"
                  style={{
                    color: active ? "rgba(247,247,246,0.7)" : "rgba(10,10,10,0.4)",
                  }}
                >
                  {pillarCounts[key]}
                </span>
              </button>
            );
          })}
        </div>

        <div
          className={`overflow-hidden transition-all duration-500 ease-out ${
            activePillar === "all" ? "max-h-0 opacity-0" : "max-h-12 opacity-100"
          }`}
        >
          <div className="px-6 md:px-10 pb-3 -mt-1 flex items-center justify-between gap-3">
            <div className="font-mono-label text-[10px] flex items-center gap-2">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: "var(--active-pillar)" }}
              />
              <span className="text-black/55">
                Showing {pillarCounts[activePillar]} of {BRANDS.length} — pillar:{" "}
              </span>
              <span style={{ color: "var(--active-pillar)" }}>
                {PILLAR_META[activePillar].label}
              </span>
            </div>
            <button
              type="button"
              data-testid="pillar-reset-inline"
              onClick={() => onPillarClick("all")}
              className="font-mono-label text-[10px] text-black/50 hover:text-black underline underline-offset-4"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Accordion list */}
      <div
        ref={gridRef}
        data-testid="foundry-accordion"
        className="border-t border-b border-black/10 bg-white/40"
      >
        {BRANDS.map((b) => (
          <AccordionRow
            key={b.id}
            b={b}
            isOpen={openId === b.id}
            onToggle={() => onToggle(b.id)}
            activePillar={activePillar}
          />
        ))}
      </div>

      {/* MATR signal strip */}
      <div
        data-testid="matr-signal-strip"
        className="mt-10 relative bg-[#0A0A0A] text-[#F7F7F6] px-6 md:px-10 py-8 md:py-10 grid grid-cols-12 gap-6 items-center"
      >
        <span
          aria-hidden
          className="absolute left-0 top-0 bottom-0 w-[3px]"
          style={{ backgroundColor: "#00D4AD" }}
        />
        <div className="col-span-12 md:col-span-2 flex items-center gap-3">
          <span
            aria-hidden
            className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: "#00D4AD" }}
          />
          <div className="font-serif-display text-[26px] leading-none">MATR</div>
        </div>
        <p className="col-span-12 md:col-span-8 text-[14px] md:text-[15px] leading-relaxed text-white/75">
          The shared signal pipeline across the entire MERA ecosystem. Every
          interaction across all ten products feeds MATR — learning how specific
          humans work, communicate, decide, and grow, and returning amplified
          intelligence back to every instrument in the ecosystem. The more
          humans in the ecosystem, the smarter every product becomes — for every
          human in it.
        </p>
        <div className="col-span-12 md:col-span-2 md:text-right">
          <span className="inline-flex items-center gap-2 font-mono-label text-[10px] uppercase tracking-[0.18em] text-white/70 border border-white/25 px-3 py-1.5">
            <span
              aria-hidden
              className="w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: "#00D4AD" }}
            />
            Signal Layer
          </span>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
        <div className="font-mono-label text-[10px] text-black/45">
          10 / 10 instruments · the foundry is complete
        </div>
        {activePillar !== "all" && (
          <button
            type="button"
            data-testid="pillar-clear"
            onClick={() => onPillarClick("all")}
            className="font-mono-label text-[10px] text-black/55 hover:text-black underline underline-offset-4"
          >
            Show all ten products
          </button>
        )}
      </div>
    </section>
  );
};
